import { AppTheme, Organization, SCBrand } from '@ngb-frontend/shared/types'

import type {
  AppLocale,
  CountryBrandRulesOverride,
  CountryBrandRulesProfile,
  CountryCode,
  CountryDateRulesOverride,
  CountryDateRulesProfile,
  CountryServiceRulesOverride,
  CountryServiceRulesProfile,
  EnabledServices,
} from '@ngb-frontend/shared/types'

// ── Section shapes ──────────────────────────────────────────────────────────
// The registry composes the existing per-domain profile types, so a country's
// full picture is just those sections side by side.

export type LocaleSettings = {
  /** ISO country used for locale/number formatting (e.g. 'se', 'gb') */
  country?: CountryCode<AppLocale>
  /** IANA timezone; absent for GLOBAL */
  tz?: string
  locales: AppLocale[]
  defaultLocale: AppLocale
}

/**
 * Country config owned by Salesforce `fe-settings` at RUNTIME for Ayvens
 * countries (fetched per session and merged over these defaults); authoritative
 * and static only for LeasePlan. Malfunctions are deliberately NOT stored here —
 * LeasePlan uses the full APP_MALFUNCTION_SETS catalogue and Ayvens receives its
 * set from Salesforce.
 */
export type RemoteCountryDefaults = {
  detailedRPV: boolean
  additionalServices: EnabledServices
  minBookingDaysDriver?: number
  minBookingDaysAgent?: number
}

export type LocaleSettingsOverride = Partial<LocaleSettings>
export type RemoteCountryDefaultsOverride = Partial<RemoteCountryDefaults>

/** The complete static settings for one country, across every domain */
export type BusinessRules = {
  org: Organization
  locale: LocaleSettings
  dates: CountryDateRulesProfile
  services: CountryServiceRulesProfile
  brand: CountryBrandRulesProfile
  remote: RemoteCountryDefaults
}

/** A country registry entry — only what differs from {@link defaultBusinessRules} */
export type BusinessRulesOverride = {
  org?: Organization
  locale?: LocaleSettingsOverride
  dates?: CountryDateRulesOverride
  services?: CountryServiceRulesOverride
  brand?: CountryBrandRulesOverride
  remote?: RemoteCountryDefaultsOverride
}

// ── Baseline ────────────────────────────────────────────────────────────────

/**
 * The default minimum booking lead days. Single-sourced because it feeds two
 * conceptually-distinct-but-coincident slots: the date-rules fallback
 * (`dates.leadDays`, used when the remote config omits a value) and the
 * LeasePlan static `remote` config.
 */
const BOOKING_MIN_DAYS = { driver: 5, agent: 0 }

/** Operating window used when no service is selected or no table matches */
export const defaultOperationTime = { minTime: '08:00', maxTime: '17:00' }

/** GLOBAL is the fallback locale (DEFAULT_COUNTRY / DEFAULT_LOCALE) */
const GLOBAL_LOCALE: LocaleSettings = {
  country: 'gb',
  locales: ['en-GLOBAL'],
  defaultLocale: 'en-GLOBAL',
}

const DEFAULT_REMOTE: RemoteCountryDefaults = {
  detailedRPV: false,
  additionalServices: {},
  minBookingDaysDriver: BOOKING_MIN_DAYS.driver,
  minBookingDaysAgent: BOOKING_MIN_DAYS.agent,
}

/** Every country inherits this and overrides only what differs. */
export const defaultBusinessRules: BusinessRules = {
  org: Organization.AYVENS,
  locale: GLOBAL_LOCALE,
  dates: {
    holidays: [],
    leadDays: {
      driver: BOOKING_MIN_DAYS.driver,
      agent: BOOKING_MIN_DAYS.agent,
      eol: { drivable: 3, notDrivable: 5 },
    },
    operatingHours: {
      any: [
        {
          type: 'PickupAndReturn',
          time: { minTime: '08:30', maxTime: '10:00' },
        },
        {
          type: 'RegularMaintenance',
          time: { minTime: '08:00', maxTime: '13:00' },
        },
        { type: 'Repair', time: { minTime: '08:00', maxTime: '13:00' } },
        { type: 'MOT', time: defaultOperationTime },
      ],
    },
    pickup: { fixedTime: '10:30', userSelectsTime: false },
  },
  services: {
    repairOffered: true,
    isDefaultRuleset: false,
    mainServicesExclusive: false,
    motExtraServicePolicy: 'default',
  },
  brand: {
    theme: AppTheme.Ayvens,
    sitecoreBrand: SCBrand.Ayvens,
    autopickSupplier: false,
    showDriverChangeWarning: true,
    showPickupAddressInSummary: true,
  },
  remote: DEFAULT_REMOTE,
}

/** LeasePlan's static remote config — Ayvens gets the equivalent from Salesforce */
export const LEASEPLAN_REMOTE: RemoteCountryDefaultsOverride = {
  additionalServices: {
    PickupAndReturn: true,
    ComplementaryCleaning: true,
    ReplacementVehicle: true,
  },
}
